import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, FileText, Loader2, Upload } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useApiHeaders } from "@/hooks/use-api-headers";
import type { TFunction } from "@/i18n/catalog";
import type { Locale } from "@/i18n/config";
import { useTranslation } from "@/i18n/use-translation";
import { apiError, showApiError } from "@/lib/api-error";

import { MenuFields, isValid, scheduleBody, type MenuDraft } from "./menu-fields";
import { useOpen } from "./menu-parts";
import {
  hasEnded,
  isLiveAt,
  menuLabel,
  predecessorOf,
  shortDate,
  type Menu,
  type MenuDetail,
} from "./menu-model";
import { useMenuDetail, useScheduleCheck } from "./use-menus";

/**
 * Upload (POS2-1456): pick a file → say how the menu runs → watch its recipes being read → done.
 * The dialog can be closed while reading; the row keeps spinning and the page toasts when it ends.
 */

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.csv,.xlsx,.xls,.docx";

type Step =
  | { step: "pick" }
  | { step: "details"; file: File; draft: MenuDraft; replace: boolean }
  | { step: "reading"; menuId: string; fileName: string };

/** Read until processed or failed. */
export function stillReading(menu: MenuDetail): boolean {
  return menu.status !== "processed" && menu.status !== "failed";
}

const DATED_FILE = /(\d{2})\.(\d{2})\.(\d{4})\s*-\s*(\d{2})\.(\d{2})\.(\d{4})/;

/**
 * The details step's starting point. A file named like `Menu 14.09.2026-27.09.2026.pdf` is a cycle
 * with those dates, coming after the latest dated cycle still running if that one ends before it
 * starts; one it overlaps runs beside it instead (POS2-1468). An undated cycle never ends, so
 * nothing follows it. Anything else is always on.
 */
export function draftFromFile(fileName: string, menus: Menu[], today: string): MenuDraft {
  const dated = fileName.match(DATED_FILE);
  const latestCycle = menus
    .filter((m) => m.cadence === "cycle" && m.ends_on !== null && !hasEnded(m, today))
    .sort((a, b) => (b.ends_on ?? "").localeCompare(a.ends_on ?? ""))[0];
  const startsOn = dated ? `${dated[3]}-${dated[2]}-${dated[1]}` : "";
  return {
    name: fileName.replace(/\.[a-z0-9]+$/i, ""),
    cadence: dated ? "cycle" : "always_on",
    startsOn,
    endsOn: dated ? `${dated[6]}-${dated[5]}-${dated[4]}` : "",
    predecessorId:
      dated && latestCycle?.ends_on && latestCycle.ends_on < startsOn ? latestCycle.id : null,
    timed: false,
    from: "11:30",
    to: "14:30",
  };
}

export function UploadFlow({
  siteId,
  siteName,
  menus,
  today,
  onClose,
  onKeepReading,
}: {
  siteId: string;
  siteName: string;
  /** The site's menus, for "Comes after". */
  menus: Menu[];
  /** The site's today (`useSiteToday`). */
  today: string;
  onClose: () => void;
  /** The dialog closed while reading: the page takes over telling the user when it ends. */
  onKeepReading: (menuId: string) => void;
}) {
  const { t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>({ step: "pick" });

  const upload = useMutation({
    mutationFn: async (v: { file: File; draft: MenuDraft; replace: boolean }) => {
      const params = new URLSearchParams({ name: v.draft.name.trim(), site_ids: siteId });
      for (const [key, value] of Object.entries(scheduleBody(v.draft))) {
        if (value !== null) params.set(key, value);
      }
      if (v.replace) params.set("replace", "true");
      const form = new FormData();
      form.append("file", v.file);
      // The browser sets the multipart Content-Type with its boundary.
      const uploadHeaders = Object.fromEntries(
        Object.entries(headers).filter(([name]) => name !== "Content-Type"),
      );
      const res = await fetch(`${API_URL}/api/v1/menus/v2/upload?${params}`, {
        method: "POST",
        headers: uploadHeaders,
        body: form,
      });
      if (!res.ok) throw await apiError(t, res);
      return (await res.json()).data as Menu;
    },
    onSuccess: (menu, v) => {
      queryClient.invalidateQueries({ queryKey: ["menus"] });
      setStep({ step: "reading", menuId: menu.id, fileName: v.file.name });
    },
    onError: showApiError,
  });

  function close() {
    if (step.step === "reading") onKeepReading(step.menuId);
    onClose();
  }

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="sm:max-w-lg">
        {step.step === "pick" && (
          <PickStep
            siteName={siteName}
            onPick={(file) =>
              setStep({
                step: "details",
                file,
                draft: draftFromFile(file.name, menus, today),
                replace: false,
              })
            }
            onCancel={onClose}
          />
        )}
        {step.step === "details" && (
          <>
            <DialogHeader>
              <DialogTitle>{t("menus.upload.detailsTitle")}</DialogTitle>
              <DialogDescription className="flex items-center gap-1.5">
                <FileText className="size-3.5" />
                {step.file.name}
              </DialogDescription>
            </DialogHeader>
            <MenuFields
              draft={step.draft}
              menus={menus}
              selfId={null}
              today={today}
              onChange={(draft) => setStep({ ...step, draft })}
            />
            <UploadCheckNote
              siteId={siteId}
              draft={step.draft}
              menus={menus}
              replace={step.replace}
              onReplace={(replace) => setStep({ ...step, replace })}
            />
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setStep({ step: "pick" })}>
                {t("menus.upload.back")}
              </Button>
              <Button
                size="sm"
                disabled={!isValid(step.draft) || upload.isPending}
                onClick={() =>
                  upload.mutate({ file: step.file, draft: step.draft, replace: step.replace })
                }
              >
                {upload.isPending ? t("menus.uploading") : t("menus.upload.readRecipes")}
              </Button>
            </DialogFooter>
          </>
        )}
        {step.step === "reading" && (
          <ReadingStep
            menuId={step.menuId}
            fileName={step.fileName}
            siteId={siteId}
            menus={menus}
            today={today}
            onClose={close}
            onDone={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function PickStep({
  siteName,
  onPick,
  onCancel,
}: {
  siteName: string;
  onPick: (file: File) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  return (
    <>
      <DialogHeader>
        <DialogTitle>{t("menus.upload.pickTitle")}</DialogTitle>
        <DialogDescription>
          {t("menus.upload.pickDescription", { site: siteName })}
        </DialogDescription>
      </DialogHeader>
      <input
        ref={input}
        type="file"
        className="hidden"
        accept={ACCEPT}
        aria-label={t("menus.upload.chooseFile")}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) onPick(file);
        }}
      />
      <button
        type="button"
        onClick={() => input.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const file = e.dataTransfer.files[0];
          if (file) onPick(file);
        }}
        className={`flex flex-col items-center rounded-lg border px-6 py-10 text-center transition-colors ${
          dragging ? "border-blue-500 bg-blue-50" : "border-gray-200 bg-gray-50 hover:bg-gray-100"
        }`}
      >
        <Upload className="size-6 text-gray-400" />
        <span className="mt-2 text-sm font-medium text-gray-900">{t("menus.upload.dropzone")}</span>
        <span className="mt-1 text-xs text-gray-400">{t("menus.upload.dropzoneHint")}</span>
      </button>
      <DialogFooter>
        <Button variant="outline" size="sm" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
      </DialogFooter>
    </>
  );
}

function ReadingStep({
  menuId,
  fileName,
  siteId,
  menus,
  today,
  onClose,
  onDone,
}: {
  menuId: string;
  fileName: string;
  siteId: string;
  menus: Menu[];
  today: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t, locale } = useTranslation();
  const openPage = useOpen();
  const queryClient = useQueryClient();
  const detail = useMenuDetail(menuId, siteId, stillReading);
  const menu = detail.data;
  const name = menu ? menuLabel(t, locale, menu) : fileName;
  const finished = !!menu && !stillReading(menu);

  // The list stops polling between extraction and processing; refresh it once reading ends.
  useEffect(() => {
    if (finished) queryClient.invalidateQueries({ queryKey: ["menus"], exact: false });
  }, [finished, queryClient]);
  const open = () => {
    onDone();
    openPage(`/menus/${menuId}`, t("menus.title"));
  };

  if (menu?.status === "failed") {
    return (
      <>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="size-5 text-red-600" />
            {t("menus.upload.failedTitle", { name })}
          </DialogTitle>
          <DialogDescription>
            {menu.error_message ?? t("menus.upload.failedHint")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onDone}>
            {t("menus.upload.done")}
          </Button>
          <Button size="sm" onClick={open}>
            {t("menus.upload.openMenu")}
          </Button>
        </DialogFooter>
      </>
    );
  }

  if (menu?.status === "processed") {
    const unmatched = menu.recipes.filter((r) => r.unmatched_ingredient_count > 0).length;
    return (
      <>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="size-5 text-emerald-600" />
            {t("menus.upload.doneTitle", { name })}
          </DialogTitle>
          <DialogDescription>
            {t("menus.upload.doneRecipes", { count: menu.recipes.length })}{" "}
            {menu.split_with.length > 0 &&
              `${t("menus.upload.splitInto", {
                names: menu.split_with.map((m) => m.display_name ?? m.name).join(", "),
              })} `}
            {goesLive(t, locale, menu, menus, siteId, today)}
          </DialogDescription>
        </DialogHeader>
        {unmatched > 0 && (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {t("menus.upload.unmatched", { count: unmatched })}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onDone}>
            {t("menus.upload.done")}
          </Button>
          <Button size="sm" onClick={open}>
            {t("menus.upload.review")}
          </Button>
        </DialogFooter>
      </>
    );
  }

  const recipes = menu?.recipes ?? [];
  const processing = menu?.status === "processing" || menu?.status === "extracted";
  const ready = recipes.filter((r) => r.processing_status === "ready").length;
  const progress = processing && recipes.length > 0 ? 40 + (60 * ready) / recipes.length : 15;
  return (
    <>
      <DialogHeader>
        <DialogTitle>{t("menus.upload.readingTitle", { name })}</DialogTitle>
        <DialogDescription>{fileName}</DialogDescription>
      </DialogHeader>
      <div className="py-2">
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <Loader2 className="size-4 animate-spin text-gray-400" />
          {processing
            ? t("menus.upload.readingMatching", { count: recipes.length })
            : t("menus.upload.readingPages")}
        </div>
        <div
          className="mt-3 h-1.5 rounded-full bg-gray-100"
          role="progressbar"
          aria-valuenow={Math.round(progress)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-1.5 rounded-full bg-emerald-500 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="mt-3 text-xs text-gray-400">{t("menus.upload.readingHint")}</p>
      </div>
      <DialogFooter>
        <Button variant="outline" size="sm" onClick={onClose}>
          {t("menus.upload.keepReading")}
        </Button>
      </DialogFooter>
    </>
  );
}

/** What the upload will do, said before anything is read: a menu with the same type and start
 * date stays beside it, unless the user replaces that menu's file instead (POS2-1469). */
function UploadCheckNote({
  siteId,
  draft,
  menus,
  replace,
  onReplace,
}: {
  siteId: string;
  draft: MenuDraft;
  menus: Menu[];
  replace: boolean;
  onReplace: (replace: boolean) => void;
}) {
  const { t, locale } = useTranslation();
  const check = useScheduleCheck(siteId, draft);
  if (!isValid(draft) || !check.data) return null;
  const { same_start } = check.data;
  if (!same_start) return null;
  const name = menuLabel(t, locale, menus.find((m) => m.id === same_start)!);
  return (
    <p className="flex items-center justify-between gap-3 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">
      {replace ? t("menus.upload.replacing", { name }) : t("menus.upload.sameStart", { name })}
      <button
        type="button"
        className="shrink-0 font-medium underline"
        onClick={() => onReplace(!replace)}
      >
        {replace ? t("menus.upload.keepBoth") : t("menus.upload.replaceIt")}
      </button>
    </p>
  );
}

/** When the menu goes live: now, on its start date, or when its predecessor ends. */
function goesLive(
  t: TFunction,
  locale: Locale,
  menu: MenuDetail,
  menus: Menu[],
  siteId: string,
  today: string,
): string {
  const predecessor = predecessorOf(menu, menus);
  if (!isLiveAt(menu, siteId) && predecessor) {
    return t("menus.upload.liveAfter", { name: menuLabel(t, locale, predecessor) });
  }
  if (hasEnded(menu, today)) return t("menus.upload.endedOff");
  if (menu.starts_on && menu.starts_on > today) {
    return t("menus.upload.liveOn", { date: shortDate(locale, menu.starts_on) });
  }
  return t("menus.upload.liveNow");
}
